import { defineStore } from 'pinia';

import { fetchWrapper } from '@/helpers';
import { router } from '@/router';
import { useAlertStore } from '@/stores';
import { apolloClient } from '@/apollo-client';
import { isTokenExpired, tokenExpiresAt } from '@/helpers/token';

let expiryTimer = null;

// Older logins stored the whole admin_user row, password hash included --
// drop it from anything already saved in this browser.
function loadStoredUser() {
    const stored = JSON.parse(localStorage.getItem('user'));
    // A login from more than 2h ago is dead; keeping it made every request
    // 401 while the page still looked logged in.
    if (stored && isTokenExpired(stored.token)) {
        localStorage.removeItem('user');
        return null;
    }
    if (stored && 'password' in stored) {
        delete stored.password;
        localStorage.setItem('user', JSON.stringify(stored));
    }
    return stored;
}

export const useAuthStore = defineStore({
    id: 'auth',
    state: () => ({
        // initialize state from local storage to enable user to stay logged in
        user: loadStoredUser(),
        returnUrl: null
    }),
    actions: {
        async login(username, password) {
            try {
                const user = await fetchWrapper.post(`${fetchWrapper.url}api/login`, { username, password });
                // update pinia state
                this.user = user;

                // store user details and jwt in local storage to keep user logged in between page refreshes
                localStorage.setItem('user', JSON.stringify(user));
                this.watchExpiry();

                // redirect to previous url or default to home page
                router.push(this.returnUrl || '/');
            } catch (error) {
                // fetch-wrapper.js's handleResponse() rejects with a plain
                // string (the backend's message) or an HTTP status number,
                // never an Error object -- so it's `error` itself that's the
                // message, not `error.message` (which is always undefined on
                // a string/number and was silently hiding the real reason).
                const alertStore = useAlertStore();
                alertStore.error(typeof error === 'string' ? error : 'Login failed. Please check your credentials and try again.');
            }
        },
        // Logs out automatically the moment the token expires, instead of
        // letting the next requests fail with 401. Safe to call repeatedly.
        watchExpiry() {
            clearTimeout(expiryTimer);
            const exp = tokenExpiresAt(this.user?.token);
            if (!exp) return;
            const ms = exp - Date.now();
            if (ms <= 0) {
                this.logout();
                return;
            }
            expiryTimer = setTimeout(() => this.logout(), Math.min(ms, 2147483647));
        },
        // Clears the session without navigating -- for the router guard,
        // which redirects to the login page itself.
        async expireSession() {
            clearTimeout(expiryTimer);
            this.user = null;
            localStorage.removeItem('user');
            localStorage.removeItem('platform');
            localStorage.removeItem('selected_stock');
            // Any alerts raised by in-flight queries that clearStore() below
            // is about to interrupt belong to the session that's ending --
            // don't let them bleed into the login screen that's about to show.
            useAlertStore().clear();
            await apolloClient.clearStore().catch(() => {});
        },
        async logout() {
            await this.expireSession();
            router.push('/account/login');
        }
    }
});
